import { useEffect, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { TRY, NUM, fmtDate, todayISO, docFileName } from "@/lib/format";
import { PageHeader, StatCard, Empty, Cell, Segmented } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, FileUp, FileDown, ShoppingCart, Receipt, CheckCircle2, XCircle, Wallet, FileSpreadsheet, Loader2, Pencil, Clock, Paperclip, ExternalLink, FileText, Send, Eye, Truck, Package, RefreshCw, Search, FlaskConical, Inbox, Percent, FileCheck2, AlertTriangle, Download, MoreHorizontal, FileArchive, BadgeCheck, Undo2, FileCode2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import HtmlPreviewDialog, { fetchPreviewUrl, fetchDraftGibPreview, buildInvoiceHtml, buildIncomingHtml } from "@/components/HtmlPreviewDialog";
import ReturnInvoiceDialog from "@/components/ReturnInvoiceDialog";
import BulkReturnDialog from "@/components/returns/BulkReturnDialog";
import { COMPANY } from "@/lib/company";
import { loadCompanyLogo } from "@/lib/branding";
import { buildInvoicesPdf, exportInvoicesExcel } from "@/lib/invoicePdf";
import { invoiceQnbStatus, INVOICE_STATUS_FILTERS } from "@/lib/qnbStatus";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@/components/DatePicker";
import { Combobox } from "@/components/Combobox";
import { contactOptions as buildContactOptions, isGenericContact } from "@/lib/contacts";
import InvoiceRecipientPicker, { blankRecipient, recipientError, recipientPayload } from "@/components/InvoiceRecipientPicker";
import QnbInvoiceDialog from "@/components/QnbInvoiceDialog";
import { BulkPayAskDialog } from "@/components/BulkPayAskDialog";
import QnbIncomingDialog, { incomingStatus, profileLabel } from "@/components/QnbIncomingDialog";
import InvoiceDetailDialog from "@/components/InvoiceDetailDialog";
import PaymentDialog, { BulkPaymentDialog } from "@/components/PaymentDialog";
import InvoiceItemsEditor, { blankInvoiceItem, ublTotals, fitToTarget, r2 } from "@/components/InvoiceItemsEditor";

const KDV_RATES = [20, 10, 1];
const PERIODS = [["all", "Tüm zamanlar"], ["month", "Bu ay"], ["3m", "Son 3 ay"], ["year", "Bu yıl"], ["custom", "Tarih aralığı"]];
const periodRange = (p) => {
  const d = new Date(); const y = d.getFullYear(); const m = d.getMonth();
  const iso = (x) => x.toLocaleDateString("sv-SE");   // yerel tarih (UTC'ye çevirince TR'de ayın son günü listeden düşüyordu)
  if (p === "month") return [iso(new Date(y, m, 1)), iso(new Date(y, m + 1, 0))];
  if (p === "3m") return [iso(new Date(y, m - 2, 1)), iso(new Date(y, m + 1, 0))];
  if (p === "year") return [`${y}-01-01`, `${y}-12-31`];
  return [null, null];
};

const PAYMENT_METHODS = [
  { value: "havale", label: "Havale/EFT" },
  { value: "nakit", label: "Nakit" },
  { value: "kredi_karti", label: "Kredi Kartı" },
  { value: "cek", label: "Çek" },
];
const fmtDT = (s) => (s ? `${fmtDate(s)}${String(s).length > 10 ? " " + String(s).slice(11, 16) : ""}` : "—");
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";
import { discountFor, productDiscountFields } from "@/lib/discountKey";
import { useInvoiceView } from "@/lib/invoiceView";

// "01/08/2026" | Date | Excel serial -> "YYYY-MM-DD"
function parseTRDate(v) {
  if (!v && v !== 0) return "";
  if (v instanceof Date) return v.toLocaleDateString("sv-SE");
  const s = String(v).trim();
  if (!s) return "";
  const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})/);
  if (m) {
    const [, d, mo, y] = m;
    const yr = y.length === 2 ? "20" + y : y;
    return `${yr}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return iso[0];
  return "";
}

function parseAmount(v) {
  if (typeof v === "number") return v;
  if (!v) return 0;
  let s = String(v).replace(/[^\d.,-]/g, "").trim();
  if (s.includes(",") && s.includes(".")) s = s.replace(/\./g, "").replace(",", ".");
  else if (s.includes(",")) s = s.replace(",", ".");
  return parseFloat(s) || 0;
}

const pick = (row, keys) => {
  for (const k of Object.keys(row)) {
    const nk = k.toLowerCase().trim();
    if (keys.some((t) => nk === t || nk.includes(t))) return row[k];
  }
  return undefined;
};

export default function Invoices({ type, variant = "full" }) {
  const isBuy = type === "alis";
  const isArchive = variant === "archive";
  const isHub = variant === "efatura";
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [efaturaRows, setEfaturaRows] = useState(null);
  const [importing, setImporting] = useState(false);
  const [sort, setSort] = useState("date_desc");
  const [payInv, setPayInv] = useState(null);
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [docInv, setDocInv] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bulkCutting, setBulkCutting] = useState(false);
  const [bulkAsk, setBulkAsk] = useState(null);   // toplu kesim öncesi ödeme sorusu
  const [qnbList, setQnbList] = useState(null);
  const [qnbListOpen, setQnbListOpen] = useState(false);
  const [tab, setTab] = useState("all");   // alış: Tümü | yeni | efatura | manuel | red
  const [stFilter, setStFilter] = useState("all");   // satış arşivi: QNB durum filtresi (e-İrsaliye ile aynı)
  const [payFilter, setPayFilter] = useState("all");  // all | open (ödenmemiş + kısmen)
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [incoming, setIncoming] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [simInfo, setSimInfo] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selIncoming, setSelIncoming] = useState(null);
  const [irsBusy, setIrsBusy] = useState(null);
  const navigate = useNavigate();
  const [detailInv, setDetailInv] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [flt, setFlt] = useState({ contact: "", period: variant === "efatura" || new URLSearchParams(window.location.search).get("q") ? "all" : "month", from: "", to: "" });   // alış & satış varsayılan: BU AY
  const [discountMap, setDiscountMap] = useState({});
  const [discountDefault, setDiscountDefault] = useState(0);
  const [invView] = useInvoiceView();
  const PAY = isBuy
    ? { paid: "Ödendi", partial: "Kısmen", unpaid: "Ödenmedi", bulkTrue: "Ödendi Yap", bulkFalse: "Ödenmedi Yap", amount: "Ödenen Tutar (₺)", paidLbl: "Ödenen", title: "Ödeme Durumu", hist: "Ödeme Geçmişi" }
    : { paid: "Tahsil edildi", partial: "Kısmen tahsil", unpaid: "Tahsil edilmedi", bulkTrue: "Tahsil Edildi Yap", bulkFalse: "Tahsil Edilmedi Yap", amount: "Tahsil Edilen Tutar (₺)", paidLbl: "Tahsil edilen", title: "Tahsilat Durumu", hist: "Tahsilat Geçmişi" };
  const blank = {
    contact_title: "", invoice_no: "", date: todayISO(), due_date: "", kdv_rate: 20, discount_rate: 0,
    paid: false, paid_amount: 0, payment_method: "havale", note: "", brand: "", show_brand: false, add_to_stock: isBuy, items: [blankInvoiceItem()],
  };
  const [form, setForm] = useState(blank);
  const [newRp, setNewRp] = useState(blankRecipient());   // yazılan ünvan kayıtlı cari değilse: fatura adresi + resmi bilgiler → cari oluşturulur
  const [contacts, setContacts] = useState([]);

  const load = () => {
    // Alış & satış: yalnızca seçili dönem sunucudan çekilir (varsayılan BU AY) — büyük arşivlerde ekran kasmaz
    const [pf, pt] = flt.period === "custom" ? [flt.from || null, flt.to || null] : periodRange(flt.period);
    const qs = new URLSearchParams({ type });
    if (pf) qs.append("start", pf);
    if (pt) qs.append("end", pt);
    api.get(`/invoices?${qs.toString()}`).then((r) => setItems((p) => (JSON.stringify(p) === JSON.stringify(r.data) ? p : r.data))).catch(() => {});
    if (isBuy) api.get("/qnb/incoming?doc_type=FATURA").then((r) => setIncoming((p) => (JSON.stringify(p) === JSON.stringify(r.data) ? p : r.data))).catch(() => {});
  };
  // QNB'de işlenen fatura varsa sık, yoksa seyrek sessiz yenileme (liste değişmezse yeniden render yok)
  const hasPending = useMemo(() => items.some((i) => i.qnb?.success && invoiceQnbStatus(i).processing), [items]);
  useAutoRefresh(load, { interval: isArchive && !hasPending ? 45000 : 15000 });
  useEffect(() => { load(); setForm(blank); setSelected(new Set()); /* eslint-disable-next-line */ }, [type]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [flt.period, flt.from, flt.to]);
  const loadContacts = () => api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  useEffect(() => { loadContacts(); if (isBuy) api.get("/qnb/status").then((r) => setCfg(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);

  const syncSales = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/qnb/sales/sync?max_docs=80");
      const parts = [data.new ? `${data.new} yeni fatura alındı` : "Yeni fatura yok — liste güncel", data.cancelled ? `${data.cancelled} iptal işlendi` : null, data.remaining ? `${data.remaining} fatura arka planda alınacak` : null].filter(Boolean);
      (data.errors || []).length ? toast.warning(`${parts.join(" · ")} · ${data.errors.slice(0, 2).join(" · ")}`) : toast.success(parts.join(" · "));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  const syncIncoming = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/qnb/incoming/sync");
      const n = (data.incoming_fatura || 0) + (data.incoming_irsaliye || 0);
      (data.errors || []).length ? toast.warning(`Senkron tamamlandı · ${data.errors.join(" · ")}`) : toast.success(n ? `${n} yeni belge alındı` : "Yeni belge yok — liste güncel");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };
  const simulateIncoming = async () => {
    if (!await confirm("TEST: Karşı firma (erpefaturatest2) bizim VKN'mize örnek bir e-Fatura gönderecek. Devam edilsin mi?")) return;
    setSimulating(true); setSimInfo(null);
    try {
      const { data } = await api.post("/qnb/simulate/incoming", { profile: "TICARIFATURA" });
      setSimInfo(data);
      if (data.mode === "local") toast.warning("QNB test2 alıcıyı tanımıyor → fatura YEREL simülasyon olarak gelen kutusuna işlendi", { duration: 8000 });
      else toast.success(`Karşı taraftan fatura gönderildi (${data.doc_id}) · 15-60 sn içinde listeye düşer`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSimulating(false); }
  };
  const toIrsaliye = async (inv) => {
    setIrsBusy(inv.id);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/to-irsaliye`);
      toast.success(data.existing ? "Bu faturanın irsaliyesi zaten var — açılıyor" : "İrsaliye taslağı oluşturuldu · şoför/plaka son kullanılan bilgilerle dolduruldu");
      navigate(`/irsaliyeler?edit=${data.irsaliye.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setIrsBusy(null); }
  };

  // ---- QNB e-Fatura / e-Arşiv ----
  const [qnbInv, setQnbInv] = useState(null);
  const openQnb = (inv) => setQnbInv(inv);
  // ---- Gelen faturadan İADE faturası (⋯ menüsü) ----
  const [returnDoc, setReturnDoc] = useState(null);
  const [bulkReturn, setBulkReturn] = useState(false);
  const [bulkEdit, setBulkEdit] = useState(null);
  const isBulkDraft = (inv) => !!(inv?.return_of?.bulk && inv.is_draft && !inv.qnb?.success);
  const openBulkEdit = (inv) => { setBulkEdit(inv); setBulkReturn(true); };
  const closeBulk = () => { setBulkReturn(false); setBulkEdit(null); };
  const [returnBusy, setReturnBusy] = useState(null);
  const openReturn = async (r) => {
    setReturnBusy(r.id);
    try {
      const { data } = await api.get(`/qnb/incoming/${r.id}`);
      if (!(data.parsed?.lines || []).length) return toast.error("Bu faturanın kalem bilgisi okunamadı (UBL ayrıştırılamadı) — iade taslağı oluşturulamıyor");
      setReturnDoc(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setReturnBusy(null); }
  };
  const openIncomingUbl = async (r) => {
    try { const { data } = await api.get(`/qnb/incoming/${r.id}/document?fmt=UBL`); window.open(fileUrl(data.url), "_blank"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const paidOf = (inv) => inv.paid_amount != null ? inv.paid_amount : (inv.paid ? inv.total : 0);
  const remainOf = (inv) => Math.max(0, (inv.total || 0) - paidOf(inv));
  const statusOf = (inv) => {
    const pa = paidOf(inv);
    if (pa >= (inv.total || 0) - 0.005) return "paid";
    return pa > 0 ? "partial" : "unpaid";
  };

  const openNew = () => { setForm(blank); setNewRp(blankRecipient()); setEditId(null); loadDiscounts(""); setDlg(true); };
  const unknownContact = !isBuy && !editId && !!form.contact_title.trim() && !isGenericContact(form.contact_title) && !contacts.some((x) => x.title === form.contact_title);
  useEffect(() => {
    if (unknownContact) setNewRp((r) => ({ ...r, mode: r.mode === "existing" && r.contact_id ? r.mode : "new", title: form.contact_title }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unknownContact, form.contact_title]);
  const openEdit = (inv) => {
    if (isBulkDraft(inv)) return openBulkEdit(inv);   // toplu iade taslağı → aynı mantıkla (kod → son alış faturası) düzenlenir
    setForm({ ...blank, ...inv, paid_amount: paidOf(inv), add_to_stock: false, items: inv.items.length ? inv.items.map((x) => ({ ...blankInvoiceItem(), ...x })) : blank.items });
    setEditId(inv.id); loadDiscounts(inv.contact_title); setDlg(true);
  };

  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((prev) => {
    const all = rows.map((r) => r.id);   // listelenen tüm satırlar (alışta gelen e-Fatura satırları "in-<id>" dahil)
    return all.length && all.every((id) => prev.has(id)) ? new Set() : new Set(all);
  });
  // seçim kümesi: fatura id'leri + gelen e-Fatura satırları ("in-<id>")
  const selInvoiceIds = () => [...selected].filter((id) => !String(id).startsWith("in-"));
  const bulkPaid = async (paid) => {
    const ids = selInvoiceIds();
    if (!ids.length) return toast.error("Seçilenler arasında kayıtlı fatura yok (gelen e-Fatura satırları için önce KABUL / alış faturasına işleyin)");
    if (paid) { setBulkPayOpen(true); return; }
    if (!await confirm(`${ids.length} faturanın ödeme kayıtları silinip "${PAY.unpaid}" durumuna alınacak. Onaylıyor musunuz?`)) return;
    await api.post("/invoices/bulk-paid", { ids, paid: false });
    toast.success(`${ids.length} fatura güncellendi`); setSelected(new Set()); load();
  };
  const bulkDelete = async () => {
    const ids = [...selected].filter((id) => { const inv = items.find((i) => i.id === id); return inv && !(inv.qnb?.success && !inv.qnb?.draft && !inv.qnb?.cancelled); });
    if (!ids.length) return toast.error("Seçilenler arasında silinebilir (kesilmemiş) fatura yok");
    if (!await confirm(`${ids.length} fatura silinecek (Çöp Kutusu'na taşınır). Onaylıyor musunuz?`)) return;
    try {
      const { data } = await api.post("/invoices/bulk-delete", { ids });
      toast.success(`${data.deleted} fatura silindi${data.skipped?.length ? ` · ${data.skipped.length} kesilmiş fatura atlandı` : ""}`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  // Toplu KABUL — seçili, yanıt bekleyen gelen e-Faturalar tek onayla sırayla KABUL edilir (her biri: GİB yanıtı + alış faturası + cari borç)
  const [bulkAccepting, setBulkAccepting] = useState(false);
  const selIncomingNew = () => incoming.filter((r) => selected.has(`in-${r.id}`) && (r.status || "yeni") === "yeni" && r.doc_type === "FATURA");
  const bulkAccept = async () => {
    const docs = selIncomingNew();
    if (!docs.length) return toast.error("Seçilenler arasında yanıt bekleyen gelen e-Fatura yok");
    const sum = docs.reduce((a, r) => a + Number(r.payable || 0), 0);
    const ticari = docs.filter((r) => (r.profile || "").toUpperCase() === "TICARIFATURA").length;
    if (!await confirm(`${docs.length} gelen e-Fatura KABUL edilecek (toplam ${TRY(sum)}).${ticari ? ` ${ticari} ticari fatura için GİB'e KABUL yanıtı gönderilir.` : ""} Her biri için alış faturası oluşturulup tedarikçi carisine borç işlenir. Bu işlem geri alınamaz — devam edilsin mi?`)) return;
    setBulkAccepting(true);
    try {
      const { data } = await api.post("/qnb/incoming/bulk-respond", { ids: docs.map((r) => r.id), response: "KABUL", add_to_stock: false });
      const fails = (data.results || []).filter((r) => !r.ok);
      if (fails.length) toast.warning(`${data.ok_count} fatura KABUL edildi · ${fails.length} hata: ${fails.slice(0, 3).map((f) => f.error).join(" · ")}`, { duration: 10000 });
      else toast.success(`${data.ok_count} gelen e-Fatura KABUL edildi · alış faturaları oluşturuldu`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkAccepting(false); }
  };

  const totals = () => ublTotals(form.items, form.kdv_rate, form.discount_rate);
  const [targetTotal, setTargetTotal] = useState("");
  const applyTarget = () => {
    const raw = String(targetTotal).trim();
    const norm = raw.includes(",") ? raw.replace(/\./g, "").replace(",", ".") : raw;
    const tv = parseFloat(norm);
    if (!(tv > 0)) { toast.error("Geçerli bir hedef Genel Toplam girin"); return; }
    if (!form.items.some((i) => Number(i.quantity) > 0 && Number(i.unit_price) > 0)) { toast.error("Önce kalem ve fiyat girin"); return; }
    // iskonto varsa iskonto oranları, yoksa (ya da hedef liste toplamını aşıyorsa) birim fiyatlar ayarlanır
    const { items: next, method, total: got } = fitToTarget(form.items, form.kdv_rate, form.discount_rate, tv);
    setForm((f) => ({ ...f, items: next, discount_rate: 0 }));
    const how = method === "price" ? "Birim fiyatlar ayarlandı" : "İskontolar ayarlandı · liste fiyatları korundu";
    if (Math.abs(got - r2(tv)) < 0.005) toast.success(`${how} · Genel Toplam ${TRY(got)}`);
    else toast.warning(`En yakın tutara getirildi: ${TRY(got)} (hedef ${TRY(tv)})`);
  };

  const loadDiscounts = async (title, id, mode) => {
    if (isBuy) return { map: {}, def: 0 };
    try {
      const p = new URLSearchParams();
      if (id) p.set("contact_id", id);
      if (title) p.set("contact_title", title);
      if (mode) p.set("mode", mode);
      const { data } = await api.get(`/discounts/resolve?${p.toString()}`); setDiscountMap(data.map || {}); setDiscountDefault(Number(data.default || 0)); return { map: data.map || {}, def: Number(data.default || 0) };
    }
    catch { setDiscountMap({}); setDiscountDefault(0); return { map: {}, def: 0 }; }
  };
  const productToItem = (p) => {
    const disc = discountFor(p, discountMap);
    return {
      name: p.name, description: "", code: p.code || "", quantity: 1, unit: (p.unit || "ADET").toUpperCase(),
      unit_price: isBuy ? Number(p.cost || p.price || 0) : Number(p.price) || 0,
      discount_rate: isBuy ? 0 : (disc != null ? Number(disc) : Number(discountDefault || 0)),
      product_id: p.id, ...productDiscountFields(p),
    };
  };
  const onContactPick = async (title) => {
    const c = contacts.find((x) => x.title === title);
    setForm((f) => ({ ...f, contact_title: title, kdv_rate: (isBuy && c?.vat_exempt) ? 0 : f.kdv_rate }));
    if (isBuy) return;
    // Satış faturası daima KDV'li → elektrikçi caride faturalı elektrikçi iskontosu (cari ID ile mükerrer ünvan karışmaz).
    const mode = c?.type === "elektrikci" ? "faturali" : "genel";
    const { map, def } = await loadDiscounts(title, c?.id, mode);
    setForm((f) => ({ ...f, items: f.items.map((r) => {
      const d = discountFor(r, map);
      if (d != null) return { ...r, discount_rate: d };
      if (def > 0 && !Number(r.discount_rate)) return { ...r, discount_rate: Number(def) };
      return r;
    }) }));
  };

  const save = async () => {
    if (!form.contact_title.trim()) return toast.error("Cari ünvanı gerekli");
    if (!isBuy && !(Number(form.kdv_rate) > 0)) return toast.error("KDV'siz satış faturası oluşturulamaz — KDV %20 seçin");
    if (unknownContact) {
      const rErr = recipientError(newRp);
      if (rErr) return toast.error(`Yeni cari: ${rErr}`);
    }
    // Yeni cari: fatura, oluşturulacak carinin (GİB sorgusuyla düzeltilmiş olabilecek) ünvanıyla kaydedilir → başıboş boş cari kalmaz
    const rpTitle = !unknownContact ? "" : newRp.mode === "new" ? newRp.title.trim() : (contacts.find((x) => x.id === newRp.contact_id)?.title || "");
    const payload = {
      ...form, type, ...(rpTitle ? { contact_title: rpTitle } : {}),
      items: form.items.filter((i) => i.name.trim()).map((i) => ({
        name: i.name, description: i.description || "", code: i.code || "",
        quantity: Number(i.quantity), unit_price: Number(i.unit_price), discount_rate: Number(i.discount_rate || 0),
        product_id: i.product_id || "", brand: i.brand || form.brand || "", unit: i.unit || "ADET",
      })),
      kdv_rate: Number(form.kdv_rate), discount_rate: Number(form.discount_rate),
      paid_amount: Number(form.paid_amount) || 0,
    };
    if (!payload.items.length) return toast.error("En az bir kalem girin");
    try {
      if (editId) await api.put(`/invoices/${editId}`, {
        contact_title: payload.contact_title, invoice_no: payload.invoice_no, date: payload.date, due_date: payload.due_date,
        items: payload.items, kdv_rate: payload.kdv_rate, discount_rate: payload.discount_rate, note: payload.note,
        payment_method: payload.payment_method,
      });
      else {
        const { data: created } = await api.post("/invoices", payload);
        if (unknownContact) {
          try {
            const { data: a } = await api.post(`/invoices/${created.id}/assign-contact`, recipientPayload(newRp));
            setContacts((cs) => [a.contact, ...cs.filter((x) => x.id !== a.contact.id)]);
            toast.success(a.matched === "new" || a.matched === "title" ? `'${a.contact.title}' carisi oluşturuldu · fatura kaydedildi` : `Fatura kayıtlı '${a.contact.title}' carisine bağlandı`);
          } catch (e) { toast.error(`Fatura kaydedildi ancak cari bilgileri kaydedilemedi: ${apiError(e)} — Fatura Kes ekranında tamamlayın`, { duration: 12000 }); }
          loadContacts();
        } else toast.success("Fatura kaydedildi");
      }
      if (editId) toast.success("Fatura güncellendi");
      setDlg(false); setForm(blank); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openPay = (inv) => setPayInv(inv);
  const del = async (id) => { if (await confirm("Faturayı sil? (Çöp Kutusu'na taşınır)")) { try { await api.delete(`/invoices/${id}`); toast.success("Fatura silindi"); load(); } catch (e) { toast.error(apiError(e)); } } };

  const qnbPdf = async (inv) => {
    try {
      const { data } = await api.get(`/invoices/${inv.id}/qnb-pdf`);
      window.open(fileUrl(data.url), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };
  const remainingOf = (inv) => Math.max(0, r2((Number(inv?.total) || 0) - (Number(inv?.paid_amount) || 0)));
  const bulkCut = () => {
    const ids = selInvoiceIds();
    if (!ids.length) return;
    const rows = items.filter((x) => ids.includes(x.id));
    setBulkAsk({ ids, count: ids.length, total: r2(rows.reduce((s, x) => s + (x.type === "satis" && !x.source_cart_id && !x.source_quick_sale_id ? remainingOf(x) : 0), 0)) });
  };
  const runBulkCut = async (pay) => {
    const { ids } = bulkAsk; setBulkAsk(null);
    setBulkCutting(true);
    let ok = 0, fail = 0, paidN = 0, payFail = 0;
    for (const id of ids) {
      try { await api.post(`/invoices/${id}/qnb-send`, { draft: false }); ok++; } catch { fail++; continue; }
      const inv = items.find((x) => x.id === id);
      const rem = inv && inv.type === "satis" && !inv.source_cart_id && !inv.source_quick_sale_id ? remainingOf(inv) : 0;
      if (pay.choice === "received" && rem > 0) {
        try { await api.post(`/invoices/${id}/payments`, { amount: rem, date: todayISO(), method: pay.method }); paidN++; } catch { payFail++; }
      }
    }
    setBulkCutting(false); setSelected(new Set()); load();
    (fail || payFail ? toast.warning : toast.success)(`${ok} fatura kesildi${fail ? ` · ${fail} başarısız` : ""}${paidN ? ` · ${paidN} faturaya ödeme işlendi` : ""}${payFail ? ` · ${payFail} faturada ödeme işlenemedi (detaydan ekleyin)` : ""}`, { duration: payFail ? 15000 : 5000 });
  };
  const openList = async (kind) => {
    const end = todayISO();
    const start = `${end.slice(0, 8)}01`;
    setQnbList({ kind, loading: true, rows: [], error: null }); setQnbListOpen(true);
    try {
      const { data } = await api.get(`/qnb/${kind}?start=${start}&end=${end}`);
      setQnbList({ kind, loading: false, rows: data.rows || [], raw: data.raw, error: null });
    } catch (e) { setQnbList({ kind, loading: false, rows: [], error: apiError(e) }); }
  };

  const openDocs = (inv) => setDocInv(inv);
  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.post(`/invoices/${docInv.id}/documents`, { url: up.data.url, name: file.name, content_type: file.type });
      toast.success("Belge eklendi"); setDocInv(data); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };
  const delDoc = async (docId) => {
    try { const { data } = await api.delete(`/invoices/${docInv.id}/documents/${docId}`); setDocInv(data); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const viewDoc = (url) => window.open(fileUrl(url), "_blank");

  // Satır içi önizleme: QNB PDF → gelen e-Fatura belgesi → ekli belge (tarama) → taslak satış: GİB görünümü → yerel şablon
  const openPreview = async (inv) => {
    setPreviewBusy(inv.id);
    const title = inv.invoice_no || inv.contact_title;
    try {
      if (inv.qnb?.ettn) { setPreview({ url: await fetchPreviewUrl(`/invoices/${inv.id}/qnb-pdf`), title }); return; }
      const inc = inv.source_type === "efatura_gelen" ? incoming.find((r) => (r.id === inv.qnb_incoming_id || r.invoice_id === inv.id) && !r.simulated) : null;
      if (inc) { try { setPreview({ url: await fetchPreviewUrl(`/qnb/incoming/${inc.id}/document`), title }); return; } catch { /* ekli belge / şablona düş */ } }
      const doc = (inv.documents || []).find((d) => /pdf|image|html/.test(d.content_type || "") || /\.(pdf|png|jpe?g|webp)$/i.test(d.url || ""));
      if (doc) { setPreview({ url: doc.url, title: `${title} · ${doc.name}` }); return; }
      if (!isBuy) {
        // Kesilecek taslak: GİB'de görüneceği şablonla (QNB ublOnizleme — belge oluşturmaz)
        try { setPreview({ ...(await fetchDraftGibPreview(inv, invView)), regen: (v) => fetchDraftGibPreview(inv, v) }); return; }
        catch (e) { toast.warning(`GİB görünümü alınamadı — yerel şablon gösteriliyor · ${apiError(e)}`); }
      }
      const c = contacts.find((x) => x.title === inv.contact_title) || {};
      const logo = await loadCompanyLogo();
      const local = (v) => ({ html: buildInvoiceHtml(inv, c, COMPANY, logo, v), title });
      setPreview({ ...local(invView), regen: async (v) => local(v) });
    } catch (e) { toast.error(apiError(e)); }
    finally { setPreviewBusy(null); }
  };

  // Gelen e-Fatura satırı önizleme: bağlı alış faturası varsa onun akışı, yoksa QNB belge görüntüsü / yerel simülasyon HTML'i
  const openIncomingPreview = async (r) => {
    setPreviewBusy(`in-${r.id}`);
    try {
      if (r.simulated) { const { data } = await api.get(`/qnb/incoming/${r.id}`); setPreview({ html: buildIncomingHtml(data), title: r.belge_no }); }
      else setPreview({ url: await fetchPreviewUrl(`/qnb/incoming/${r.id}/document`), title: r.belge_no });
    } catch (e) { toast.error(apiError(e)); }
    finally { setPreviewBusy(null); }
  };

  // Toplu indirme: seçili varsa seçilenler, yoksa süzülen liste (cari + dönem + arama); gelen e-Faturaya bağlı alış faturaları dahil
  const listedInvoices = () => rows.flatMap((r) => (r.kind === "incoming" ? (r.invoice ? [r.invoice] : []) : [r]));
  const exportTargets = () => (selected.size ? items.filter((i) => selected.has(i.id)) : listedInvoices());
  const exportRange = () => (flt.period === "custom" ? [flt.from || null, flt.to || null] : periodRange(flt.period));
  const exportLabel = () => {
    const [from, to] = exportRange();
    const parts = [flt.contact || "Tüm cariler", from || to ? `${from ? fmtDate(from) : "…"} – ${to ? fmtDate(to) : "…"}` : "Tüm zamanlar"];
    if (selected.size) parts.push(`${selected.size} seçili fatura`);
    return parts.join(" · ");
  };
  const exportFile = (ext) => {
    const one = exportTargets();
    if (one.length === 1) return docFileName(one[0].contact_title, one[0].qnb?.invoice_no || one[0].invoice_no || "Fatura", ext);   // tek fatura: cari adı + belge no
    const slug = (s) => String(s || "").toLocaleLowerCase("tr").replace(/[^a-z0-9ğüşıöç]+/gi, "-").replace(/^-|-$/g, "").slice(0, 40);
    const [from, to] = exportRange();
    return [`faturalar-${type}`, flt.contact ? slug(flt.contact) : "", from || "", to || "", selected.size ? `secili-${selected.size}` : ""].filter(Boolean).join("_") + "." + ext;
  };
  const doExportExcel = () => {
    const list = exportTargets();
    if (!list.length) return toast.error("İndirilecek fatura yok");
    exportInvoicesExcel(list, { contacts, filename: exportFile("xlsx") });
    toast.success(`${list.length} fatura Excel olarak indirildi`);
  };
  const doExportPdf = async () => {
    const list = exportTargets();
    if (!list.length) return toast.error("İndirilecek fatura yok");
    if (list.length > 150 && !await confirm(`${list.length} fatura tek PDF'te birleştirilecek; bu biraz sürebilir. Devam edilsin mi?`)) return;
    setExporting(true);
    try {
      const pdf = await buildInvoicesPdf(list, { contacts, subtitle: exportLabel() });
      pdf.save(exportFile("pdf"));
      toast.success(`${list.length} fatura tek PDF olarak indirildi`);
    } catch (e) { toast.error("PDF oluşturulamadı: " + (e?.message || e)); }
    finally { setExporting(false); }
  };
  // Resmî GİB/QNB PDF'leri: satış → kesilmiş faturalar; alış → gelen e-Faturalar (TEST simülasyonu hariç). Seçili yoksa süzülen liste.
  const gibTargets = () => {
    if (isBuy) {
      if (selected.size) {
        const ids = [...selected].flatMap((id) => (String(id).startsWith("in-") ? [String(id).slice(3)] : incoming.filter((r) => r.invoice_id === id).map((r) => r.id)));
        return [...new Set(ids)].filter((id) => !incoming.find((r) => r.id === id)?.simulated);
      }
      return rows.filter((r) => r.kind === "incoming" && !r.doc.simulated).map((r) => r.doc.id);
    }
    return exportTargets().filter((i) => i.qnb?.success && i.qnb?.ettn && !i.qnb?.draft).map((i) => i.id);
  };
  const doExportGib = async (mode) => {
    const ids = gibTargets();
    if (!ids.length) return toast.error(isBuy ? "Resmî PDF'i alınabilecek gelen e-Fatura yok (TEST simülasyonları hariç)" : "Listede QNB'den kesilmiş fatura yok");
    if (ids.length > 30 && !await confirm(`${ids.length} belgenin resmî GİB PDF'i QNB'den alınacak; bu biraz sürebilir. Devam edilsin mi?`)) return;
    setExporting(true);
    try {
      const res = await api.post("/qnb/bulk-documents", { kind: isBuy ? "incoming" : "outgoing", ids, mode }, { responseType: "blob", timeout: 240000 });
      const name = ((res.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/) || [])[1] || `faturalar-gib.${mode === "zip" ? "zip" : "pdf"}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      const n = Number(res.headers["x-doc-count"] || ids.length), err = Number(res.headers["x-doc-errors"] || 0);
      (err ? toast.warning : toast.success)(`${n} resmî GİB PDF ${mode === "zip" ? "ZIP olarak" : "tek dosyada birleştirilerek"} indirildi${err ? ` · ${err} belge alınamadı` : ""}`);
    } catch (e) {
      let msg = apiError(e);
      if (e?.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* json değil */ } }
      toast.error(msg);
    } finally { setExporting(false); }
  };

  const importExcel = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "array" });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        const parsed = rows.map((r) => ({
          name: r["Ürün"] || r["Malzeme"] || r["name"] || "",
          description: r["Açıklama"] || r["description"] || "",
          quantity: Number(r["Miktar"] || r["quantity"] || 1),
          unit_price: Number(r["Birim Fiyat"] || r["Fiyat"] || r["unit_price"] || 0),
        })).filter((i) => i.name);
        if (!parsed.length) return toast.error("Geçerli kalem bulunamadı");
        setForm({ ...blank, items: parsed });
        setDlg(true);
        toast.success(`${parsed.length} kalem yüklendi, kaydetmek için onaylayın`);
      } catch { toast.error("Excel okunamadı"); }
    };
    reader.readAsArrayBuffer(file); e.target.value = "";
  };
  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([{ "Ürün": "NYA Kablo 2.5mm", "Açıklama": "Bakır kablo", "Miktar": 100, "Birim Fiyat": 12 }]);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Fatura");
    XLSX.writeFile(wb, "fatura-sablonu.xlsx");
  };

  // GİB E-Fatura / E-Arşiv toplu içe aktarım — format otomatik algılanır
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const importGib = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "array", cellDates: true });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
        if (!rows.length) return toast.error("Excel boş görünüyor.");
        const headerStr = Object.keys(rows[0]).join("|").toLowerCase();
        const isEarsiv = headerStr.includes("ödenecek tutar") || headerStr.includes("odenecek tutar") || headerStr.includes("vergiler toplam");
        const source_type = isEarsiv ? "earsiv" : "efatura";
        const parsed = rows.map((r) => {
          const title = String(pick(r, ["alıcı unvan", "alici unvan", "alıcı unvanı", "gönderen unvan", "gonderen unvan", "unvan", "ad soyad"]) || "").trim();
          const kind = String(pick(r, ["fatura tipi"]) || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
          let net = null, kdv = null, total = 0;
          if (isEarsiv) {
            net = round2(parseAmount(pick(r, ["toplam tutar"])));
            kdv = round2(parseAmount(pick(r, ["vergiler toplam"])));
            total = round2(parseAmount(pick(r, ["ödenecek tutar", "odenecek tutar"])) || net + kdv);
          } else {
            total = round2(parseAmount(pick(r, ["ödenecek tutar", "odenecek tutar", "tutar", "genel toplam"])));
          }
          return {
            contact_title: title,
            invoice_no: String(pick(r, ["fatura no", "fatura numar", "belge no"]) || "").trim(),
            date: parseTRDate(pick(r, ["fatura tarih", "düzenlenme tarih", "duzenlenme tarih", "fatura düzenlenme"])),
            total, net, kdv,
            tax_no: String(pick(r, ["vkn", "tckn", "vergi"]) || "").trim(),
            due_date: parseTRDate(pick(r, ["son ödeme", "son odeme", "vade"])),
            type, source_type, _kind: kind,
          };
        }).filter((r) => r.contact_title && r.total > 0 && !r._kind.includes("iade"));
        if (!parsed.length) return toast.error("Geçerli fatura satırı bulunamadı. Lütfen GİB E-Fatura/E-Arşiv 'giden fatura' Excel dosyasını yükleyin.");
        setEfaturaRows(parsed);
        toast.success(`${parsed.length} ${isEarsiv ? "E-Arşiv" : "E-Fatura"} faturası okundu. Önizleyip kaydedin.`);
      } catch (err) { toast.error("Excel okunamadı: " + apiError(err)); }
    };
    reader.readAsArrayBuffer(file); e.target.value = "";
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/invoices/import", efaturaRows.map(({ _kind, ...r }) => r));
      toast.success(`${data.count} ${isBuy ? "alış" : "satış"} faturası içe aktarıldı${data.skipped ? ` · ${data.skipped} mükerrer atlandı` : ""}`);
      setEfaturaRows(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setImporting(false); }
  };

  const downloadEfaturaTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([{
      "Alıcı VKN/TCKN": "4910317513", "Alıcı Unvan/Ad Soyad": "KALECIK GRUP İNŞAAT LTD. ŞTİ.",
      "Fatura No": "GEA2026000000149", "Fatura Tipi": "Satış", "Fatura Türü": "Ticari",
      "Fatura Tarihi": "03/08/2026", "Tutar": 9084.00, "Döviz Cinsi": "TRY", "Son Ödeme Tarihi": "",
    }]);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Sheet0");
    XLSX.writeFile(wb, "e-fatura-giden-sablon.xlsx");
  };
  const downloadEarsivTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([{
      "Alıcı VKN/TCKN": "1841789373", "Alıcı Unvanı /Adı Soyadı": "BOZKENT GRUP İNŞAAT LTD.ŞTİ",
      "Fatura Numarası": "GAA2026000000044", "Fatura Düzenlenme Tarihi": "18/08/2026",
      "Toplam Tutar": 17916.67, "Vergiler Toplamı": 3583.33, "Ödenecek Tutar": 21500.00, "Para Birimi": "TRY",
    }]);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Sheet0");
    XLSX.writeFile(wb, "e-arsiv-giden-sablon.xlsx");
  };
  const efaturaTotal = (efaturaRows || []).reduce((s, r) => s + r.total, 0);
  const dispNet = (r) => r.net != null ? r.net : round2(r.total / (1 + (r.kdv_rate ?? 20) / 100));
  const dispKdv = (r) => r.kdv != null ? r.kdv : round2(r.total - dispNet(r));

  const inRange = (inv) => {
    if (flt.contact && inv.contact_title !== flt.contact) return false;
    let [from, to] = flt.period === "custom" ? [flt.from || null, flt.to || null] : periodRange(flt.period);
    const d = String(inv.date || "").slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  };
  const baseItems = isArchive ? items : isHub ? items.filter((i) => !i.qnb?.success) : items;
  const view = baseItems.filter(inRange);
  const totalAmount = view.reduce((s, i) => s + i.total, 0);
  const unpaid = view.reduce((s, i) => s + remainOf(i), 0);
  const t = totals();
  const contactOptions = useMemo(() => [...new Set([...items.map((i) => i.contact_title), ...incoming.map((r) => r.sender_name)].filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")).map((c) => ({ value: c, label: c })), [items, incoming]);

  const rows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    const hit = (...vals) => !q || vals.some((v) => String(v || "").toLocaleLowerCase("tr").includes(q));
    let arr = baseItems.filter(inRange).filter((i) => hit(i.contact_title, i.invoice_no, i.contact_tax_no)).map((i) => ({ ...i, kind: "invoice", _d: String(i.date || "").slice(0, 10), _c: i.created_at || "" }));
    if (isArchive && stFilter !== "all") arr = arr.filter((i) => invoiceQnbStatus(i).key === stFilter);
    if (payFilter === "open") arr = arr.filter((i) => statusOf(i) !== "paid");
    if (isBuy) {
      const linkedIds = new Set(incoming.map((r) => r.invoice_id).filter(Boolean));
      arr = arr.filter((i) => i.source_type !== "efatura_gelen" || !linkedIds.has(i.id));
      const fmtIn = (s) => (s && String(s).length >= 8 && !String(s).includes("-") ? `${String(s).slice(0, 4)}-${String(s).slice(4, 6)}-${String(s).slice(6, 8)}` : String(s || "").slice(0, 10));
      const inc = incoming
        .filter((r) => hit(r.sender_name, r.belge_no, r.sender_vkn) && (!flt.contact || r.sender_name === flt.contact || r.contact_title === flt.contact))
        .map((r) => ({ kind: "incoming", id: `in-${r.id}`, doc: r, invoice: items.find((i) => i.id === r.invoice_id) || null, contact_title: r.sender_name, invoice_no: r.belge_no, total: Number(r.payable || 0), _d: fmtIn(r.belge_tarihi), _c: r.gelis_tarihi || r.created_at || "" }))
        .filter((r) => inRange({ date: r._d, contact_title: flt.contact ? flt.contact : "" }));
      arr = [...arr, ...inc];
      if (payFilter === "open") arr = arr.filter((r) => r.kind !== "incoming" || !r.invoice || statusOf(r.invoice) !== "paid");
      if (tab === "yeni") arr = arr.filter((r) => r.kind === "incoming" && (r.doc.status || "yeni") === "yeni");
      else if (tab === "efatura") arr = arr.filter((r) => r.kind === "incoming" || r.source_type === "efatura_gelen");
      else if (tab === "manuel") arr = arr.filter((r) => r.kind === "invoice" && r.source_type !== "efatura_gelen");
      else if (tab === "red") arr = arr.filter((r) => r.kind === "incoming" && r.doc.status === "red");
    }
    const byDate = (a, b) => (b._d.localeCompare(a._d)) || String(b._c).localeCompare(String(a._c));
    if (sort === "amount_desc") return arr.sort((a, b) => (b.total || 0) - (a.total || 0));
    if (sort === "date_asc") return arr.sort((a, b) => -byDate(a, b));
    if (sort === "contact_az") return arr.sort((a, b) => (a.contact_title || "").localeCompare(b.contact_title || "", "tr"));
    if (sort === "contact_volume") {
      const vol = {};
      arr.forEach((i) => { vol[i.contact_title] = (vol[i.contact_title] || 0) + (i.total || 0); });
      return arr.sort((a, b) => ((vol[b.contact_title] || 0) - (vol[a.contact_title] || 0)) || byDate(a, b));
    }
    return arr.sort(byDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, incoming, sort, isArchive, isHub, isBuy, flt, search, tab, stFilter, payFilter]);

  const buyTabCounts = useMemo(() => ({
    all: baseItems.length + incoming.length,
    yeni: incoming.filter((r) => (r.status || "yeni") === "yeni").length,
    efatura: incoming.length,
    manuel: baseItems.filter((i) => i.source_type !== "efatura_gelen").length,
    red: incoming.filter((r) => r.status === "red").length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [items, incoming]);

  // ---- kart satırları (e-İrsaliye tasarımı) ----
  const stripeOf = (inv) => {
    if (isBuy) return inv.source_type === "efatura_gelen" ? "bg-indigo-500" : "bg-slate-400";
    return invoiceQnbStatus(inv).stripe;
  };
  const payBadge = (inv, clickable = true) => {
    const st = statusOf(inv);
    return (
      <button onClick={() => clickable && openPay(inv)} data-testid={`toggle-paid-${inv.id}`} className={`inline-flex flex-col items-start gap-0.5 ${clickable ? "" : "cursor-default"}`}>
        {st === "paid" ? <Badge variant="outline" className="cursor-pointer border-emerald-300 bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" />{PAY.paid}</Badge>
          : st === "partial" ? <Badge variant="outline" className="cursor-pointer border-amber-300 bg-amber-50 text-amber-700" data-testid={`partial-${inv.id}`}><Clock className="h-3 w-3 mr-1" />{PAY.partial}</Badge>
          : <Badge variant="destructive" className="cursor-pointer"><XCircle className="h-3 w-3 mr-1" />{PAY.unpaid}</Badge>}
        {st === "partial" && <span className="text-[11px] text-amber-600 tabular-nums" data-testid={`remaining-${inv.id}`}>Kalan {TRY(remainOf(inv))}</span>}
      </button>
    );
  };
  const renderInvoiceRow = (inv) => {
    const q = inv.qnb || {};
    const names = (inv.items || []).map((x) => x.name).filter(Boolean);
    const locked = q.success && !q.draft;
    const st = invoiceQnbStatus(inv);
    const StIcon = st.icon;
    return (
      <div key={inv.id} className={`group relative rounded-xl border bg-card pl-5 pr-4 py-3.5 overflow-hidden transition-all hover:shadow-md hover:-translate-y-px ${selected.has(inv.id) ? "ring-2 ring-secondary/40" : ""}`} data-testid={`invoice-${inv.id}`}>
        <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${stripeOf(inv)}`} />
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <Checkbox className="shrink-0" checked={selected.has(inv.id)} onCheckedChange={() => toggleSel(inv.id)} data-testid={`select-${inv.id}`} />
          <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3 min-w-0">
            <Cell label="Cari" className="col-span-2 md:col-span-1">
              <div className="font-head font-black text-[15px] leading-tight truncate" title={inv.contact_title}>{inv.contact_title}</div>
              <div className="text-[11px] text-muted-foreground font-mono truncate">{contacts.find((c) => c.title === inv.contact_title)?.tax_no || q.receiver?.vkn || "VKN yok"}</div>
            </Cell>
            <Cell label="Fatura No">
              {inv.invoice_no ? <div className="font-mono text-sm font-bold tabular-nums truncate">{inv.invoice_no}</div> : <div className="text-xs italic text-muted-foreground">Kesimde atanır</div>}
              <div className="flex flex-wrap gap-1 mt-0.5">
                {inv.source_type === "efatura_gelen" && <Badge variant="outline" className="text-[10px] border-indigo-300 bg-indigo-50 text-indigo-700" data-testid={`incoming-src-badge-${inv.id}`}>QNB e-Fatura</Badge>}
                {isBuy && inv.source_type !== "efatura_gelen" && <Badge variant="outline" className="text-[10px] border-slate-300 bg-slate-50 text-slate-600" data-testid={`manual-badge-${inv.id}`}>Manuel</Badge>}
                {!isBuy && !q.success && <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-700" data-testid={`draft-badge-${inv.id}`}>Taslak</Badge>}
                {q.success && <Badge variant="outline" className={`text-[10px] ${q.cancelled ? "border-red-300 bg-red-50 text-red-600" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`} data-testid={`qnb-badge-${inv.id}`}>{q.cancelled ? "İptal" : q.channel_label}{q.draft ? " taslak" : ""}</Badge>}
                {q.imported && <Badge variant="outline" className="text-[10px] border-violet-300 bg-violet-50 text-violet-700" title="QNB Portal'dan otomatik alındı" data-testid={`imported-badge-${inv.id}`}>QNB Portal</Badge>}
                {(q.type_code === "IADE" || inv.type_code === "IADE") && <Badge className="text-[10px] bg-orange-600 hover:bg-orange-600 text-white" data-testid={`iade-badge-${inv.id}`}>İADE{inv.return_of?.refs?.length > 1 ? ` · ${inv.return_of.refs.length} fatura` : inv.return_of?.ref_invoice_no ? ` · ${inv.return_of.ref_invoice_no}` : ""}</Badge>}
                {(inv.irsaliye_ids || []).length > 0 && <Badge variant="outline" className="text-[10px] border-sky-300 bg-sky-50 text-sky-700" data-testid={`irs-badge-${inv.id}`}><Truck className="h-3 w-3 mr-0.5" />İrsaliye</Badge>}
                {(inv.documents?.length || 0) > 0 && <Badge variant="outline" className="text-[10px] text-muted-foreground" title={`${inv.documents.length} belge`}><Paperclip className="h-3 w-3 mr-0.5" />{inv.documents.length}</Badge>}
              </div>
            </Cell>
            <Cell label="Tarih · Kalem">
              <div className="text-sm tabular-nums font-semibold">{fmtDate(inv.date)}{inv.due_date ? <span className="text-muted-foreground font-normal text-xs"> · vade {fmtDate(inv.due_date)}</span> : null}</div>
              <div className="text-[11px] text-muted-foreground truncate" title={names.join(", ")}><Package className="h-3 w-3 inline mr-1 -mt-0.5" />{names.length} kalem{names.length ? ` · ${names.slice(0, 2).join(", ")}${names.length > 2 ? "…" : ""}` : ""}</div>
            </Cell>
            <Cell label="Tutar">
              <div className="font-head font-black text-[15px] tabular-nums">{TRY(inv.total)}</div>
              <div className="text-[11px] text-muted-foreground tabular-nums">Net {TRY(inv.net)} · KDV {TRY(inv.kdv)}</div>
            </Cell>
            {isArchive ? (
              <Cell label="Durum">
                <div className="flex flex-col items-start gap-1">
                  <Badge variant="outline" className={`${st.cls} font-bold`} data-testid={`invoice-status-${inv.id}`}>{st.processing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <StIcon className="h-3 w-3 mr-1" />}{st.label}</Badge>
                  {payBadge(inv, true)}
                </div>
                {q.status_label && <div className="text-[10px] text-muted-foreground mt-0.5 truncate" title={q.status_label} data-testid={`archive-status-${inv.id}`}>{q.status_label}{q.status?.gib_kodu ? ` · GİB ${q.status.gib_kodu}` : ""}</div>}
              </Cell>
            ) : (
              <Cell label={PAY.title}>
                {payBadge(inv, true)}
              </Cell>
            )}
          </div>
          <div className="flex items-center gap-1 lg:justify-end shrink-0 lg:pl-3 lg:border-l flex-wrap">
            {isHub && <Button size="sm" className="h-8" onClick={() => openQnb(inv)} data-testid={`qnb-inv-${inv.id}`}><Send className="h-3.5 w-3.5 mr-1" />Kes</Button>}
            {isBulkDraft(inv) && <Button size="sm" variant="outline" className="h-8 border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800" onClick={() => openBulkEdit(inv)} data-testid={`bulk-edit-${inv.id}`} title="Toplu iade taslağına ürün ekle / çıkar"><Undo2 className="h-3.5 w-3.5 mr-1" />Ürün Ekle</Button>}
            <Button size={isArchive ? "sm" : "icon"} variant={isArchive ? "outline" : "ghost"} className={isArchive ? "h-8" : "h-8 w-8"} onClick={() => openPreview(inv)} disabled={previewBusy === inv.id} data-testid={`preview-inv-${inv.id}`} title="Önizle (indirmeden görüntüle)">{previewBusy === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}{isArchive && <span className="ml-1">Önizle</span>}</Button>
            {isArchive ? (
              <>
                <Button size="sm" variant={q.success ? "outline" : "default"} className="h-8" onClick={() => openQnb(inv)} data-testid={`archive-view-${inv.id}`} title={q.success ? "QNB takip · durum, PDF, tarihçe, iptal" : "Taslak faturayı QNB'den kes"}><Send className="h-3.5 w-3.5 mr-1" />{q.success ? "Takip" : "Kes"}</Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`archive-more-${inv.id}`} title="Diğer işlemler"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => setDetailInv(inv)} data-testid={`detail-inv-${inv.id}`}><FileCheck2 className="h-4 w-4 mr-2 text-secondary" />Detaylı görüntüle</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => qnbPdf(inv)} data-testid={`archive-pdf-${inv.id}`}><FileText className="h-4 w-4 mr-2" />Fatura PDF (QNB)</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => toIrsaliye(inv)} disabled={irsBusy === inv.id} data-testid={`to-irsaliye-${inv.id}`}><Truck className="h-4 w-4 mr-2" />İrsaliye oluştur</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openDocs(inv)} data-testid={`docs-inv-${inv.id}`}><Paperclip className="h-4 w-4 mr-2" />Belgeler{(inv.documents?.length || 0) > 0 ? ` (${inv.documents.length})` : ""}</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDetailInv(inv)} data-testid={`detail-inv-${inv.id}`} title="Detaylı görüntüle"><FileCheck2 className="h-4 w-4 text-secondary" /></Button>
                {!isHub && !isBuy && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openQnb(inv)} data-testid={`qnb-inv-${inv.id}`} title="QNB"><Send className="h-4 w-4 text-secondary" /></Button>}
                <Button size="icon" variant="ghost" className="h-8 w-8 relative" onClick={() => openDocs(inv)} data-testid={`docs-inv-${inv.id}`} title="Belgeler">
                  <Paperclip className="h-4 w-4" />
                  {(inv.documents?.length || 0) > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-secondary text-white text-[9px] font-bold flex items-center justify-center">{inv.documents.length}</span>}
                </Button>
                {!locked && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(inv)} data-testid={`edit-inv-${inv.id}`}><Pencil className="h-4 w-4" /></Button>}
                {(!locked || q.cancelled) && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(inv.id)} data-testid={`del-inv-${inv.id}`} title="Sil"><Trash2 className="h-4 w-4 text-destructive" /></Button>}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };
  const renderIncomingRow = (row) => {
    const r = row.doc;
    const st = incomingStatus(r.status);
    const stripe = (r.status || "yeni") === "yeni" ? "bg-sky-500" : r.status === "kabul" ? "bg-emerald-500" : "bg-red-500";
    const linked = row.invoice;
    const isNew = (r.status || "yeni") === "yeni";
    return (
      <div key={`in-${r.id}`} className={`group relative rounded-xl border bg-card pl-5 pr-4 py-3.5 overflow-hidden transition-all hover:shadow-md hover:-translate-y-px ${!r.read ? "bg-primary/[0.03]" : ""}`} data-testid={`incoming-row-${r.id}`}>
        <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${stripe}`} />
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <Checkbox className="shrink-0 self-start lg:self-center" checked={selected.has(`in-${r.id}`)} onCheckedChange={() => toggleSel(`in-${r.id}`)} data-testid={`select-in-${r.id}`} />
          <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3 min-w-0">
            <Cell label="Gönderen" className="col-span-2 md:col-span-1">
              <div className="font-head font-black text-[15px] leading-tight truncate flex items-center gap-1.5" title={r.sender_name}>{r.sender_name}{!r.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" title="Okunmadı" />}</div>
              <div className="text-[11px] text-muted-foreground font-mono truncate">{r.sender_vkn}</div>
            </Cell>
            <Cell label="Belge No">
              <div className="font-mono text-sm font-bold tabular-nums truncate">{r.belge_no}</div>
              <div className="flex flex-wrap gap-1 mt-0.5">
                <Badge variant="outline" className="text-[10px] border-indigo-300 bg-indigo-50 text-indigo-700">Gelen e-Fatura</Badge>
                <Badge variant="outline" className="text-[10px]">{profileLabel(r.profile)}</Badge>
                {r.simulated && <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-700">TEST</Badge>}
              </div>
            </Cell>
            <Cell label="Tarih · Geliş">
              <div className="text-sm tabular-nums font-semibold">{fmtDate(r.belge_tarihi)}</div>
              <div className="text-[11px] text-muted-foreground">Geliş {fmtDT(r.gelis_tarihi)}</div>
            </Cell>
            <Cell label="Tutar">
              <div className="font-head font-black text-[15px] tabular-nums">{TRY(r.payable)}</div>
              <div className="text-[11px] text-muted-foreground">{linked ? <span className="text-emerald-700 font-semibold inline-flex items-center gap-1"><Receipt className="h-3 w-3" />Alış faturası işlendi{linked.stock_applied ? " · stokta" : ""}</span> : "Alış faturası oluşmadı"}</div>
            </Cell>
            <Cell label="Durum">
              <Badge variant="outline" className={st.cls} data-testid={`incoming-badge-${r.id}`}>{st.label}</Badge>
              {linked && <div className="mt-1">{payBadge(linked)}</div>}
            </Cell>
          </div>
          <div className="flex items-center gap-1 lg:justify-end shrink-0 lg:pl-3 lg:border-l">
            {!isNew && (
              <span className={`inline-flex items-center h-8 px-2.5 rounded-md text-xs font-bold select-none ${r.status === "kabul" ? "bg-emerald-600 text-white" : "bg-red-100 text-red-700"}`} data-testid={`incoming-answered-${r.id}`}>
                {r.status === "kabul" ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}{r.status === "kabul" ? "KABUL ✓" : "RED"}
              </span>
            )}
            <Button size="sm" variant={isNew ? "default" : "outline"} className="h-8" onClick={() => setSelIncoming(r)} data-testid={`incoming-open-${r.id}`}><Eye className="h-3.5 w-3.5 mr-1" />{isNew ? "İncele & Yanıtla" : "Takip"}</Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => (linked ? openPreview(linked) : openIncomingPreview(r))} disabled={previewBusy === `in-${r.id}` || previewBusy === linked?.id} data-testid={`preview-incoming-${r.id}`} title="Fatura görüntüsünü önizle">{previewBusy === `in-${r.id}` || (linked && previewBusy === linked.id) ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`incoming-more-${r.id}`} title="Diğer işlemler">{returnBusy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}</Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem onClick={() => openReturn(r)} disabled={returnBusy === r.id} className="text-orange-700 focus:text-orange-800 focus:bg-orange-50 font-semibold" data-testid={`incoming-return-${r.id}`}><Undo2 className="h-4 w-4 mr-2" />İade Faturası Oluştur</DropdownMenuItem>
                <DropdownMenuSeparator />
                {linked && <DropdownMenuItem onClick={() => setDetailInv(linked)} data-testid={`detail-inv-${linked.id}`}><Receipt className="h-4 w-4 mr-2 text-secondary" />Alış faturası detayı</DropdownMenuItem>}
                {linked && <DropdownMenuItem onClick={() => openPay(linked)} data-testid={`incoming-pay-${r.id}`}><Wallet className="h-4 w-4 mr-2" />Ödeme kaydet</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => (linked ? openPreview(linked) : openIncomingPreview(r))} data-testid={`incoming-gib-view-${r.id}`}><Eye className="h-4 w-4 mr-2" />GİB fatura görüntüsü</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openIncomingUbl(r)} disabled={r.simulated} data-testid={`incoming-ubl-${r.id}`}><FileCode2 className="h-4 w-4 mr-2" />UBL (XML) indir</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-6 stagger-in" data-testid={`invoices-${type}`}>
      <PageHeader
        title={isBuy ? "Alış Faturaları" : isHub ? "Fatura Kes (e-Fatura / e-Arşiv)" : "Satış Faturaları"}
        badge={isBuy ? "Gelen Kutusu" : isHub ? "QNB Kesim" : "GİB Arşivi"}
        subtitle={isBuy ? "QNB'den gelen e-Faturalar ve manuel/kâğıt alış faturaları tek listede — KABUL edilen e-Fatura otomatik alış faturası + cari borç olur" : isHub ? "Yalnızca kesilmeyi bekleyen taslaklar. Tür (e-Fatura / e-Arşiv) canlı mükellef sorgusuyla otomatik belirlenir; kesilen faturalar Satış Faturaları'na geçer" : "Kesilen e-Fatura / e-Arşiv faturaları — tüm ayrıntılar, durum, önizleme, belgeler ve tek tıkla irsaliye"}
      >
        {isArchive ? (
          <>
            <Button variant="outline" onClick={syncSales} disabled={syncing} data-testid="sales-sync-btn" title="QNB Portal / e-Arşiv'de kesilen tüm satış faturalarını sisteme al"><RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />QNB'den Çek</Button>
            <Button variant="outline" onClick={() => openList("outgoing")} data-testid="archive-report"><FileDown className="h-4 w-4 mr-2" />QNB Giden Rapor</Button>
          </>
        ) : (
          <>
            {isBuy && cfg?.simulator && <Button variant="outline" onClick={simulateIncoming} disabled={simulating} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="incoming-simulate-btn">{simulating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-2" />}Test: Karşı taraftan fatura</Button>}
            {isBuy && <Button variant="outline" onClick={syncIncoming} disabled={syncing} data-testid="incoming-sync-btn">{syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}QNB'den Çek</Button>}
            {!isBuy && <>
              <Button variant="outline" onClick={downloadTemplate}><FileDown className="h-4 w-4 mr-2" />Kalem Şablonu</Button>
              <label>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} data-testid="inv-import" />
                <Button variant="outline" asChild><span><FileUp className="h-4 w-4 mr-2" />Kalemli Excel</span></Button>
              </label>
              <Button variant="outline" onClick={downloadEfaturaTemplate} data-testid="efatura-template"><FileDown className="h-4 w-4 mr-2" />E-Fatura Şablon</Button>
              <Button variant="outline" onClick={downloadEarsivTemplate} data-testid="earsiv-template"><FileDown className="h-4 w-4 mr-2" />E-Arşiv Şablon</Button>
              <label>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importGib} data-testid="gib-import" />
                <Button asChild className="bg-secondary hover:bg-secondary/90"><span><FileSpreadsheet className="h-4 w-4 mr-2" />Excel'den Aktar</span></Button>
              </label>
            </>}
            {isHub && <Button variant="outline" onClick={() => openList("outgoing")} data-testid="qnb-outgoing-btn"><FileDown className="h-4 w-4 mr-2" />Giden Rapor</Button>}
            {isHub && <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800" onClick={() => setBulkReturn(true)} data-testid="bulk-return-btn"><Undo2 className="h-4 w-4 mr-2" />Toplu İade Faturası</Button>}
            <Button onClick={openNew} data-testid="add-invoice-btn"><Plus className="h-4 w-4 mr-2" />{isBuy ? "Manuel Alış Faturası" : "Yeni Fatura"}</Button>
          </>
        )}
      </PageHeader>

      {isBuy && cfg && !cfg.efatura_ready && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 flex items-center gap-3" data-testid="incoming-config-warning">
          <AlertTriangle className="h-5 w-5 shrink-0" />Gelen e-Fatura için QNB e-Fatura connector yapılandırması gerekli (Kullanıcılar → QNB paneli).
        </div>
      )}
      {simInfo && (
        <div className={`rounded-xl border p-4 text-sm space-y-1 ${simInfo.mode === "local" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`} data-testid="incoming-sim-result">
          <div className="font-bold flex items-center gap-2"><FlaskConical className="h-4 w-4" />Test gönderimi: {simInfo.doc_id} · {simInfo.sender_title} → bize · {TRY(simInfo.totals?.payable)} · QNB durumu: {simInfo.status_label}</div>
          {simInfo.hint && <div className="text-xs">{simInfo.hint}</div>}
        </div>
      )}

      <div className={`grid grid-cols-2 gap-4 ${isBuy ? "lg:grid-cols-5" : "lg:grid-cols-4"}`} data-testid="inv-kpis">
        <StatCard label={isBuy ? "Toplam Alış" : isHub ? "Kesilecek Toplam" : "Toplam Satış"} value={TRY(totalAmount)} icon={isBuy ? ShoppingCart : Receipt} tone={isBuy ? "blue" : "success"} sub={`${view.length} fatura`} testid="inv-kpi-total" onClick={() => setPayFilter("all")} active={payFilter === "all"} />
        <StatCard label={isBuy ? "Ödenmemiş" : "Tahsil Edilecek"} value={TRY(unpaid)} icon={Wallet} tone="danger" sub={`${view.filter((i) => statusOf(i) !== "paid").length} açık fatura · tıkla süz`} testid="inv-kpi-unpaid" onClick={() => setPayFilter((p) => (p === "open" ? "all" : "open"))} active={payFilter === "open"} />
        <StatCard label="Toplam KDV" value={TRY(view.reduce((s, i) => s + (i.kdv || 0), 0))} icon={Percent} testid="inv-kpi-kdv" />
        {isBuy ? <>
          <StatCard label="Yanıt Bekleyen e-Fatura" value={incoming.filter((r) => (r.status || "yeni") === "yeni").length} icon={Inbox} tone="blue" testid="inv-kpi-pending" onClick={() => setTab("yeni")} active={tab === "yeni"} />
          <StatCard label="Kabul / Red" value={`${incoming.filter((r) => r.status === "kabul").length} / ${incoming.filter((r) => r.status === "red").length}`} icon={CheckCircle2} tone="success" testid="inv-kpi-answered" />
        </> : isHub ? (
          <StatCard label="Taslak" value={rows.length} icon={Clock} tone="primary" sub="kesilmeyi bekliyor" testid="inv-kpi-draft" />
        ) : (
          <StatCard label="e-Fatura / e-Arşiv" value={`${view.filter((i) => i.qnb?.channel === "efatura").length} / ${view.filter((i) => i.qnb?.channel === "earsiv").length}`} icon={FileCheck2} tone="primary" testid="inv-kpi-channels" />
        )}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="inv-toolbar">
        <div className="flex items-center gap-2 flex-wrap">
          {isArchive && (
            <div data-testid="inv-status-filters">
              <Segmented value={stFilter} onChange={setStFilter} testid="inv-status" size="xs" options={INVOICE_STATUS_FILTERS.map(([k, l]) => [k, l])} />
            </div>
          )}
          {isBuy && (
            <div data-testid="inv-tabs">
              <Segmented value={tab} onChange={setTab} testid="inv-tab" size="xs" options={[["all", "Tümü", buyTabCounts.all], ["yeni", "Yanıt Bekleyen", buyTabCounts.yeni], ["efatura", "e-Fatura (QNB)", buyTabCounts.efatura], ["manuel", "Manuel / Kâğıt", buyTabCounts.manuel], ["red", "Reddedilen", buyTabCounts.red]]} />
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap" data-testid="inv-filters">
            <div className="w-56"><Combobox options={[{ value: "", label: "Tüm cariler" }, ...contactOptions]} value={flt.contact} onChange={(v) => setFlt({ ...flt, contact: v })} placeholder="Cari filtrele" searchPlaceholder="Cari ara..." data-testid="inv-filter-contact" /></div>
            <Select value={flt.period} onValueChange={(v) => setFlt({ ...flt, period: v })}>
              <SelectTrigger className="h-9 w-40" data-testid="inv-filter-period"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            {flt.period === "custom" && <>
              <DatePicker value={flt.from} onChange={(v) => setFlt({ ...flt, from: v })} placeholder="Başlangıç" data-testid="inv-filter-from" />
              <DatePicker value={flt.to} onChange={(v) => setFlt({ ...flt, to: v })} placeholder="Bitiş" data-testid="inv-filter-to" />
            </>}
            {(flt.contact || flt.period !== "all" || payFilter !== "all") && <Button size="sm" variant="ghost" onClick={() => { setFlt({ contact: "", period: "all", from: "", to: "" }); setPayFilter("all"); }} data-testid="inv-filter-clear">Temizle</Button>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari, fatura no, VKN / TCKN ara..." className="pl-9 h-9" data-testid="inv-search" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-52" data-testid="inv-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="date_desc">Tarih: Yeni → Eski</SelectItem>
              <SelectItem value="date_asc">Tarih: Eski → Yeni</SelectItem>
              <SelectItem value="contact_volume">En çok işlem yapılan cari</SelectItem>
              <SelectItem value="amount_desc">Tutar: Yüksek → Düşük</SelectItem>
              <SelectItem value="contact_az">Cari Ünvanı: A → Z</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9" onClick={load} title={hasPending ? "QNB'de işlenen fatura var: 15 sn'de bir otomatik güncellenir" : "Listeyi yenile"} data-testid="inv-refresh-btn"><RefreshCw className={`h-4 w-4 ${hasPending ? "animate-spin [animation-duration:3s]" : ""}`} /><span className="hidden sm:inline ml-1.5">Yenile</span></Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 border-secondary/40 text-secondary hover:bg-secondary/5" disabled={exporting} data-testid="inv-export-menu">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                <span className="ml-1.5">İndir{selected.size ? ` (${selected.size} seçili)` : ` (${listedInvoices().length})`}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <div className="px-2 py-1.5 text-[11px] text-muted-foreground">{selected.size ? "Seçili faturalar" : "Süzülen liste"} · {exportLabel()}</div>
              <DropdownMenuItem onClick={doExportExcel} data-testid="inv-export-excel"><FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />Excel (.xlsx) — faturalar + kalemler</DropdownMenuItem>
              <DropdownMenuItem onClick={doExportPdf} data-testid="inv-export-pdf"><FileText className="h-4 w-4 mr-2 text-red-600" />Tek PDF — tüm faturalar arka arkaya</DropdownMenuItem>
              {!isHub && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Resmî GİB / QNB belgeleri{isBuy ? " (gelen e-Fatura)" : ""}</div>
                  <DropdownMenuItem onClick={() => doExportGib("merged")} data-testid="inv-export-gib-pdf"><BadgeCheck className="h-4 w-4 mr-2 text-secondary" />Resmî GİB PDF — tek birleşik dosya</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => doExportGib("zip")} data-testid="inv-export-gib-zip"><FileArchive className="h-4 w-4 mr-2 text-amber-600" />Resmî GİB PDF'ler — ZIP (ayrı dosyalar)</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-secondary text-white rounded-lg px-4 py-3" data-testid="bulk-bar">
          <span className="font-semibold text-sm">{selected.size} {isBuy && [...selected].some((id) => String(id).startsWith("in-")) ? "belge" : "fatura"} seçildi</span>
          <div className="ml-auto flex gap-2 flex-wrap">
            {isBuy && selIncomingNew().length > 0 && (
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white font-black" onClick={bulkAccept} disabled={bulkAccepting} data-testid="bulk-accept-incoming">
                {bulkAccepting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}{bulkAccepting ? "KABUL yanıtları gönderiliyor…" : `Seçilenleri KABUL Et (${selIncomingNew().length})`}
              </Button>
            )}
            {isHub && <Button size="sm" variant="secondary" className="bg-white text-secondary hover:bg-white/90" onClick={bulkCut} disabled={bulkCutting} data-testid="bulk-cut"><Send className="h-4 w-4 mr-1" />{bulkCutting ? "Kesiliyor..." : "Seçilenleri Kes"}</Button>}
            <Button size="sm" variant="outline" className="border-white/40 text-white hover:bg-white/10" onClick={doExportExcel} data-testid="bulk-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
            <Button size="sm" variant="outline" className="border-white/40 text-white hover:bg-white/10" onClick={doExportPdf} disabled={exporting} data-testid="bulk-export-pdf">{exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}Tek PDF</Button>
            {!isHub && <Button size="sm" variant="outline" className="border-white/40 text-white hover:bg-white/10" onClick={() => doExportGib("merged")} disabled={exporting} data-testid="bulk-gib-pdf"><BadgeCheck className="h-4 w-4 mr-1" />GİB PDF</Button>}
            {!isHub && <Button size="sm" variant="outline" className="border-white/40 text-white hover:bg-white/10" onClick={() => doExportGib("zip")} disabled={exporting} data-testid="bulk-gib-zip"><FileArchive className="h-4 w-4 mr-1" />GİB ZIP</Button>}
            <Button size="sm" variant="secondary" className="bg-white text-secondary hover:bg-white/90" onClick={() => bulkPaid(true)} data-testid="bulk-paid-true"><CheckCircle2 className="h-4 w-4 mr-1" />{PAY.bulkTrue}</Button>
            <Button size="sm" variant="outline" className="border-white/40 text-white hover:bg-white/10" onClick={() => bulkPaid(false)} data-testid="bulk-paid-false"><XCircle className="h-4 w-4 mr-1" />{PAY.bulkFalse}</Button>
            <Button size="sm" variant="outline" className="border-red-300 text-red-100 hover:bg-red-500/30" onClick={bulkDelete} data-testid="bulk-delete"><Trash2 className="h-4 w-4 mr-1" />Seçilenleri Sil</Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => setSelected(new Set())}>Temizle</Button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <Empty title={payFilter === "open" ? "Açık fatura yok" : isArchive ? (stFilter !== "all" || search || flt.contact || flt.period !== "all" ? "Eşleşen fatura yok" : "Kesilen fatura yok") : isHub ? "Kesilmeyi bekleyen taslak yok" : "Fatura yok"} desc={payFilter === "open" ? "Bu listede ödenmemiş / kısmen ödenmiş fatura bulunmuyor." : isArchive ? (stFilter !== "all" || search ? "Filtreyi veya aramayı değiştirin." : "Henüz QNB'den kesilmiş fatura yok. Fatura kesme işlemi 'Fatura Kes' sayfasından yapılır.") : isHub ? "Yeni Fatura ile taslak oluşturun; kesilen faturalar Satış Faturaları'nda listelenir." : "QNB'den gelen e-Fatura bekleniyor ya da manuel alış faturası ekleyin."} />
      ) : (
        <div className="space-y-2.5" data-testid="inv-list">
          <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground px-1 flex-wrap" data-testid="inv-list-summary">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer w-fit"><Checkbox checked={rows.length > 0 && rows.every((r) => selected.has(r.id))} onCheckedChange={toggleAll} data-testid="select-all" />Tümünü seç</label>
              <span><b className="text-foreground tabular-nums">{rows.length}</b> {isBuy ? "kayıt" : "fatura"} listeleniyor{payFilter === "open" ? " · yalnızca açık olanlar" : ""}</span>
            </div>
            <span className="tabular-nums">Liste toplamı: <b className="text-foreground">{TRY(rows.reduce((s, r) => s + (r.total || 0), 0))}</b></span>
          </div>
          {rows.map((row) => row.kind === "incoming" ? renderIncomingRow(row) : renderInvoiceRow(row))}
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-[min(64rem,96vw)] max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">{editId ? "Faturayı Düzenle" : (isBuy ? "Manuel Alış Faturası" : "Yeni Satış Faturası")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <F className="sm:col-span-2" label="Cari Ünvanı * (ünvan, VKN veya TCKN ile arayın)">
              <Combobox
                options={buildContactOptions(contacts, (c) => (c.vat_exempt ? { disabled: true, disabledReason: isBuy ? "KDV'siz tedarikçi — Cari Hesaplar → Faturasız Alımlar bölümünü kullanın" : "KDV'siz cariye fatura kesilemez" } : {}))}
                value={form.contact_title}
                onChange={onContactPick}
                allowCustom
                placeholder="Cari seçin veya yeni ünvan yazın"
                searchPlaceholder="Ünvan / VKN / TCKN ara..."
                emptyText="Cari bulunamadı — yazıp ekleyin"
                data-testid="inv-contact"
              />
              {contacts.find((x) => x.title === form.contact_title)?.vat_exempt && <p className="text-[11px] text-destructive font-semibold mt-1" data-testid="inv-contact-vat-warning">Bu cari KDV'siz: fatura düzenlenemez.</p>}
            </F>
            {unknownContact && (
              <div className="sm:col-span-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="inv-new-contact-panel">
                <p className="text-xs text-amber-950 flex gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" /><span><b>{form.contact_title}</b> kayıtlı bir cari değil — <b>fatura adresini</b> ve resmi bilgileri girin; kaydedince cari oluşturulur ve fatura bu cariye bağlanır.</span></p>
                <InvoiceRecipientPicker value={newRp} onChange={setNewRp} contacts={contacts} data-testid="inv-new-contact" />
              </div>
            )}
            <F label="Fatura No">{editId ? <Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="inv-no" /> : <Input value="QNB otomatik atayacak" disabled className="italic text-muted-foreground" data-testid="inv-no-auto" />}</F>
            <F label="Tarih"><DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} data-testid="inv-date" /></F>
            <F label="Vade Tarihi"><DatePicker value={form.due_date || ""} onChange={(v) => setForm({ ...form, due_date: v })} placeholder="Vade seçin" data-testid="inv-due-date" /></F>
            <F className="sm:col-span-2" label="Ödeme Yöntemi">
              <Select value={form.payment_method || "havale"} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="inv-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>

          <InvoiceItemsEditor items={form.items} onChange={(items) => setForm((f) => ({ ...f, items }))} productToItem={productToItem} testPrefix="item" />

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <F label="Genel İskonto (%)"><Input type="number" value={form.discount_rate} onChange={(e) => setForm({ ...form, discount_rate: e.target.value })} data-testid="inv-global-discount" /></F>
            {isBuy ? (
              <>
                <F label="KDV (%)"><Input type="number" value={form.kdv_rate} onChange={(e) => setForm({ ...form, kdv_rate: e.target.value })} data-testid="inv-kdv-rate" /></F>
                <div className="flex items-center gap-2 sm:pt-6">
                  <Switch checked={Number(form.kdv_rate) === 0} onCheckedChange={(v) => setForm({ ...form, kdv_rate: v ? 0 : 20 })} data-testid="inv-vat-exempt" />
                  <Label className="text-sm">KDV'siz (%0)</Label>
                </div>
              </>
            ) : (
              <F label="KDV (%) — zorunlu">
                <Select value={String(KDV_RATES.includes(Number(form.kdv_rate)) ? form.kdv_rate : 20)} onValueChange={(v) => setForm({ ...form, kdv_rate: Number(v) })}>
                  <SelectTrigger data-testid="inv-kdv-rate"><SelectValue /></SelectTrigger>
                  <SelectContent>{KDV_RATES.map((r) => <SelectItem key={r} value={String(r)}>%{r}{r === 20 ? " (varsayılan)" : ""}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">KDV'siz satış faturası oluşturulamaz.</p>
              </F>
            )}
            {!isBuy && (
              <div className="flex items-center gap-2 sm:pt-6">
                <Switch checked={!!form.show_brand} onCheckedChange={(v) => setForm({ ...form, show_brand: v })} data-testid="inv-show-brand" />
                <Label className="text-sm">Faturada marka adı görünsün</Label>
              </div>
            )}
            {!editId && (
              <F label={PAY.amount}>
                <div className="flex gap-1">
                  <Input type="number" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} data-testid="inv-paid-amount" />
                  <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={() => setForm({ ...form, paid_amount: t.total.toFixed(2) })}>Tümü</Button>
                </div>
              </F>
            )}
            {isBuy && !editId && (
              <div className="flex items-center gap-2 sm:pt-6">
                <Switch checked={form.add_to_stock} onCheckedChange={(v) => setForm({ ...form, add_to_stock: v })} data-testid="inv-stock" />
                <Label className="text-sm">Stoğa ekle</Label>
              </div>
            )}
            {isBuy && !editId && form.add_to_stock && (
              <F className="sm:col-span-2" label="Marka (yeni ürünler için — opsiyonel)"><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="inv-brand" placeholder="Ör. VİKO, ACK" /></F>
            )}
          </div>

          <div className="bg-muted/50 rounded-lg p-4 space-y-1 text-sm">
            <Line label="Liste Toplamı" value={TRY(t.sub)} />
            <Line label={`İskonto (satır${Number(form.discount_rate) ? ` + %${form.discount_rate} genel` : ""})`} value={`- ${TRY(t.disc)}`} />
            <Line label="Net" value={TRY(t.net)} />
            <Line label={`KDV (%${form.kdv_rate})`} value={TRY(t.kdv)} />
            <div className="flex justify-between pt-2 border-t font-head font-black text-lg"><span>Genel Toplam</span><span className="tabular-nums text-primary">{TRY(t.total)}</span></div>
            {!isBuy && (
              <div className="flex items-end gap-2 pt-2 mt-1 border-t" data-testid="inv-target-wrap">
                <div className="flex-1 min-w-0">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Hedef Genel Toplam (KDV dahil)</Label>
                  <Input value={targetTotal} onChange={(e) => setTargetTotal(e.target.value)} placeholder="Ör. 45000" inputMode="decimal" className="h-9 mt-0.5" data-testid="inv-target-total" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyTarget(); } }} />
                </div>
                <Button type="button" size="sm" variant="secondary" className="shrink-0 h-9" onClick={applyTarget} data-testid="inv-target-apply"><Percent className="h-3.5 w-3.5 mr-1" />Tutara Eşitle</Button>
              </div>
            )}
            {!isBuy && <p className="text-[11px] text-muted-foreground leading-snug" data-testid="inv-target-help">İskonto yoksa birim fiyatlar, iskonto varsa satır iskonto oranları orantılı ayarlanır; satır-bazlı (GİB) toplam tam bu rakama oturur.</p>}
            {!editId && Number(form.paid_amount) > 0 && (
              <>
                <Line label={PAY.paidLbl} value={`- ${TRY(Number(form.paid_amount))}`} />
                <div className="flex justify-between font-semibold text-amber-600"><span>Kalan Bakiye</span><span className="tabular-nums">{TRY(Math.max(0, t.total - Number(form.paid_amount)))}</span></div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>İptal</Button>
            <Button onClick={save} data-testid="save-invoice">Faturayı Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* E-Fatura / E-Arşiv import preview */}
      <Dialog open={!!efaturaRows} onOpenChange={(o) => !o && setEfaturaRows(null)}>
        <DialogContent className="max-w-[96vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="efatura-preview">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">E-Fatura / E-Arşiv İçe Aktarım Önizleme</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            {efaturaRows?.length} fatura <b>{isBuy ? "alış" : "satış"} faturası</b> olarak eklenecek.
            {" "}Kaynak: <b>{efaturaRows?.[0]?.source_type === "earsiv" ? "E-Arşiv" : "E-Fatura"}</b>.
            {efaturaRows?.[0]?.source_type === "earsiv" ? " KDV, dosyadaki 'Vergiler Toplamı' sütunundan alınır." : " Tutar KDV dahil kabul edilir (KDV %20 ayrıştırılır)."}
          </p>
          <div className="border rounded-lg overflow-x-auto max-h-[55vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Cari Ünvanı</TableHead>
                  <TableHead>Fatura No</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead>VKN/TCKN</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="text-right">KDV</TableHead>
                  <TableHead className="text-right">Genel Toplam</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(efaturaRows || []).map((r, i) => (
                  <TableRow key={i} data-testid={`efatura-row-${i}`}>
                    <TableCell className="font-medium max-w-[220px] truncate">{r.contact_title}</TableCell>
                    <TableCell className="text-xs font-mono">{r.invoice_no || "-"}</TableCell>
                    <TableCell className="text-sm">{r.date ? fmtDate(r.date) : "-"}</TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{r.tax_no || "-"}</TableCell>
                    <TableCell className="text-right tabular-nums">{TRY(dispNet(r))}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{TRY(dispKdv(r))}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{TRY(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-3">
            <span className="font-semibold">Toplam ({efaturaRows?.length} fatura)</span>
            <span className="font-head font-black text-lg tabular-nums text-primary">{TRY(efaturaTotal)}</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEfaturaRows(null)}>İptal</Button>
            <Button onClick={confirmImport} disabled={importing} data-testid="efatura-confirm">
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
              {efaturaRows?.length} Faturayı İçe Aktar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fatura ödemeleri — ortak diyalog: hedef (fatura önseçili) + tutar (kalan önerilir) + yöntem */}
      <PaymentDialog open={!!payInv} onClose={() => setPayInv(null)} contactTitle={payInv?.contact_title} invoice={payInv} invoices={items.filter((i) => i.contact_title === payInv?.contact_title)} onSaved={() => load()} />
      <BulkPaymentDialog open={bulkPayOpen} onClose={() => setBulkPayOpen(false)} invoices={items.filter((i) => selected.has(i.id))} isBuy={isBuy} onDone={() => { setSelected(new Set()); load(); }} />

      {/* Fatura belgeleri (PDF/görsel) */}
      <Dialog open={!!docInv} onOpenChange={(o) => !o && setDocInv(null)}>
        <DialogContent className="max-w-md" data-testid="docs-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Fatura Belgeleri</DialogTitle></DialogHeader>
          {docInv && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">{docInv.contact_title} · {docInv.invoice_no || "Fatura"}</div>
              <div className="space-y-1.5">
                {(docInv.documents || []).length === 0 ? <p className="text-sm text-muted-foreground py-2">Henüz belge eklenmedi.</p> : (docInv.documents || []).map((d) => (
                  <div key={d.id} className="flex items-center gap-2 border rounded-lg px-3 py-2" data-testid={`doc-${d.id}`}>
                    <FileText className="h-4 w-4 text-secondary shrink-0" />
                    <span className="text-sm truncate flex-1">{d.name}</span>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => viewDoc(d.url)} data-testid={`view-doc-${d.id}`}><ExternalLink className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => delDoc(d.id)} data-testid={`del-doc-${d.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                ))}
              </div>
              <label>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" className="hidden" onChange={(e) => { uploadDoc(e.target.files[0]); e.target.value = ""; }} data-testid="doc-file-input" />
                <Button asChild disabled={uploading} className="w-full">
                  <span>{uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Paperclip className="h-4 w-4 mr-2" />}Belge Yükle (PDF/Görsel)</span>
                </Button>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocInv(null)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QNB gelen / giden liste */}
      <Dialog open={qnbListOpen} onOpenChange={(o) => { if (!o) { setQnbListOpen(false); setQnbList(null); } }}>
        <DialogContent className="max-w-[96vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qnb-list-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">{qnbList?.kind === "inbox" ? "Gelen e-Faturalar" : "Giden Fatura Raporu"}</DialogTitle></DialogHeader>
          {qnbList?.loading ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : qnbList?.error ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="qnb-list-error">{qnbList.error}</div>
          ) : (qnbList?.rows || []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Kayıt bulunamadı.{qnbList?.raw ? ` (${String(qnbList.raw).slice(0, 200)})` : ""}</p>
          ) : (
            <div className="border rounded-lg overflow-x-auto max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader><TableRow className="bg-muted/50"><TableHead>No</TableHead><TableHead>Tarih</TableHead><TableHead>Cari</TableHead><TableHead className="text-right">Tutar</TableHead><TableHead>Durum</TableHead></TableRow></TableHeader>
                <TableBody>
                  {qnbList.rows.map((r, i) => (
                    <TableRow key={i} data-testid={`qnb-list-row-${i}`}>
                      <TableCell className="text-xs font-mono">{r.no || "-"}</TableCell>
                      <TableCell className="text-sm">{r.date || "-"}</TableCell>
                      <TableCell className="text-sm max-w-[220px] truncate">{r.title || r.vkn || "-"}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.total != null ? TRY(r.total) : "-"}</TableCell>
                      <TableCell className="text-xs">{r.status || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => { setQnbListOpen(false); setQnbList(null); }}>Kapat</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkPayAskDialog ask={bulkAsk} onCancel={() => setBulkAsk(null)} onConfirm={runBulkCut} />
      <QnbInvoiceDialog invoice={qnbInv} contacts={contacts} onClose={() => setQnbInv(null)} onDone={() => { load(); loadContacts(); }} onEditBulk={(inv) => { setQnbInv(null); openBulkEdit(inv); }} />
      <InvoiceDetailDialog invoice={detailInv} contacts={contacts} onClose={() => setDetailInv(null)} onQnb={(inv) => { setDetailInv(null); openQnb(inv); }} onAddPayment={(inv) => { setDetailInv(null); openPay(inv); }} />
      <QnbIncomingDialog doc={selIncoming} onClose={() => setSelIncoming(null)} onDone={load} />
      <ReturnInvoiceDialog doc={returnDoc} onClose={() => setReturnDoc(null)} onCreated={(inv) => { setReturnDoc(null); load(); openQnb(inv); }} />
      <BulkReturnDialog open={bulkReturn} editInv={bulkEdit} onClose={closeBulk} onCreated={(inv) => { closeBulk(); load(); openQnb(inv); }} />
      <HtmlPreviewDialog open={!!preview} title={preview?.title} url={preview?.url} html={preview?.html} regen={preview?.regen} onClose={() => setPreview(null)} />
    </div>
  );
}

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
const Line = ({ label, value }) => (
  <div className="flex justify-between text-muted-foreground"><span>{label}</span><span className="tabular-nums">{value}</span></div>
);
